import React, { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  DataImport,
} from 'graphql-common';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentWithRightSidebar from '@lib/layouts/DashboardLayout/DashboardContentWithRightSidebar/DashboardContentWithRightSidebar';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import { FormRef } from '@lib/components/ReactHookForm/types';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import Trans from '@lib/components/Trans/Trans';
import { IconName } from '@lib/components/Modal/enums';
import ImportErrorsModal from 'routes/DataImport/components/ImportErrorsModal/ImportErrorsModal';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import DownloadTemplateButton from './components/DownloadTemplateButton/DownloadTemplateButton';
import ImportHistory from './components/ImportHistory/ImportHistory';
import ImportForm from './components/ImportForm/ImportForm';
import useActions from './utils/useActions';

const modals = [Modals.DataImportDestroyModal, Modals.DataImportErrorsModal];

export default function DataImportComponent() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const useModalManagementResult = useModalManagement(modals);
  const useBulkSelectResult = useTableItemsBulkSelect<DataImport>();
  const {
    activeImportId,
    config,
    dataImportQuery,
    dataImportsQuery,
    importFormLoading,
    onCancelDataImport,
    onCloseDataImportErrorsModal,
    onCloseDestroyDataImportModal,
    onConfirmedDestroyDataImport,
    onDestroyDataImport,
    onResetForm,
    onShowDataImportErrors,
    onSubmitDataImportForm,
  } = useActions({
    useModalManagementResult,
    useBulkSelectResult,
    formRef,
  });
  const breadCrumbsData = getBreadCrumbsDataByKey({
    pathname,
    t,
  });
  const activeImportData = useMemo(() => {
    return activeImportId ? dataImportQuery.data : undefined;
  }, [activeImportId, dataImportQuery.data]);

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isSitesAvailable =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) ===
    AccessLevelEnum.Write;
  const isAssetsAvailable =
    getUserAccessByComponent(AccessComponentEnum.AssetsManagement) ===
    AccessLevelEnum.Write;

  const content = (
    <>
      <PageHeader title={t('import')}>
        {isSitesAvailable || isAssetsAvailable ? (
          <DownloadTemplateButton
            isSitesAvailable={isSitesAvailable}
            isAssetsAvailable={isAssetsAvailable}
          />
        ) : undefined}
      </PageHeader>
      <PageBody>
        {dataImportsQuery.firstLoading ? (
          <CircleLoader />
        ) : (
          <ImportHistory
            dataImportsQuery={dataImportsQuery}
            isAssetsAvailable={isAssetsAvailable}
            isSitesAvailable={isSitesAvailable}
            onCancelDataImport={onCancelDataImport}
            onDestroyDataImport={onDestroyDataImport}
            onShowDataImportErrors={onShowDataImportErrors}
          />
        )}
      </PageBody>
    </>
  );

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {isSitesAvailable || isAssetsAvailable ? (
        <DashboardContentWithRightSidebar
          sidebar={
            <ImportForm
              activeImport={activeImportData}
              formRef={formRef}
              isAssetsAvailable={isAssetsAvailable}
              isLoading={importFormLoading}
              isSitesAvailable={isSitesAvailable}
              onResetForm={onResetForm}
              onShowDataImportErrors={onShowDataImportErrors}
              onSubmit={onSubmitDataImportForm}
            />
          }
          useSidebarHeader={false}
          toggleBtnTop={62.5}
        >
          {content}
        </DashboardContentWithRightSidebar>
      ) : (
        content
      )}
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-data-import-text" />}
        icon={IconName.Delete}
        isOpen={useModalManagementResult.isModalOpened(
          Modals.DataImportDestroyModal,
        )}
        onClose={onCloseDestroyDataImportModal}
        onConfirm={onConfirmedDestroyDataImport}
        title={t('are-you-sure')}
      />
      <ImportErrorsModal
        isOpen={useModalManagementResult.isModalOpened(
          Modals.DataImportErrorsModal,
        )}
        onClose={onCloseDataImportErrorsModal}
        dataImport={useBulkSelectResult.selectedSingleTableItem}
        activeImportId={activeImportId}
        config={config}
      />
    </>
  );
}
